import { useEffect, useState } from 'react';
import { useCountdown } from '../hooks/useCountdown';
import type { ViewId } from '../state';
import { api } from '../lib/api';

type Props = {
    onNavigate: (id: ViewId) => void;
    onShowRules: () => void;
    target: Date;
    bank: number;
    /**
     * When true, the rules popup is shown on first mount — used when the
     * user lands on the view via the channel autopost deeplink so they get
     * the "как участвовать" explainer right away, per Интеграция с каналом.md.
     */
    autoShowRules?: boolean;
};

/**
 * BIG BOG GIVEAWAY: weekly raffle that pays out a percentage of the running
 * pot to 71 winners (1x10% / 5x5% / 65x1%). Tickets are auto-issued — one
 * per fresh referral the user brings during the round — and the pot is fed
 * by 1% commissions from BOG BATTLE and FAST GIVEAWAY. The actual draw
 * fires from a server tick at round end.
 */
export function BigGiveawayView({ onNavigate, onShowRules, target, bank, autoShowRules }: Props) {
    const [data, setData] = useState<Awaited<ReturnType<typeof api.getBig>> | null>(null);
    const [history, setHistory] = useState<Array<{ id: number; drawn_at: string; pot: number }> | null>(null);

    const refresh = async () => {
        try {
            const r = await api.getBig();
            setData(r);
        } catch { /* keep prior */ }
    };

    useEffect(() => {
        void refresh();
        api.bigHistory().then((r) => setHistory(r.items)).catch(() => setHistory([]));
    }, []);

    // Channel-button deeplink → auto-show the rules popup once. The parent
    // resets autoShowRules after mount so a second visit to the view via the
    // menu doesn't reopen it.
    useEffect(() => {
        if (autoShowRules) onShowRules();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoShowRules]);

    const endsAt = data?.round.endsAt ? new Date(data.round.endsAt) : target;
    const countdown = useCountdown(endsAt);
    const pot = data?.round.pot ?? bank;
    const myTickets = data?.myTickets ?? 0;
    const totalTickets = data?.totalTickets ?? 0;
    const refsThisRound = data?.refsThisRound ?? 0;

    return (
        <div className="view">
            <h2 className="logo text-center" style={{ fontSize: 20 }}>BIG BOG GIVEAWAY</h2>

            <div className="giveaway-bank">
                <div className="giveaway-bank-amount">
                    {pot.toLocaleString('ru-RU')} <small style={{ fontSize: 16 }}>BSP</small>
                </div>
            </div>

            <div className="info-card text-center">
                Банк копится с каждой <b>BOG BATTLE</b> и <b>FAST GIVEAWAY</b>.<br/>
                Каждый приглашённый реферал — это 1 бесплатный билет в этот раунд.
            </div>

            <div className="info-card">
                <h3 className="text-center text-accent fw-900 f-12" style={{ letterSpacing: 1 }}>
                    ПРИЗЫ (% ОТ БАНКА)
                </h3>
                <PrizeRow label="1 билет" pct="10%" />
                <PrizeRow label="5 билетов" pct="5%" />
                <PrizeRow label="65 билетов" pct="1%" />
                <div className="text-muted text-center" style={{ fontSize: 10 }}>
                    1 игрок получит 10%, пятеро по 5%, шестьдесят пять — по 1%.
                </div>
            </div>

            <div className="info-card">
                <div className="info-row"><span className="info-label">Мои билеты</span><span className="info-value">{myTickets}</span></div>
                <div className="info-row"><span className="info-label">Привёл за раунд</span><span className="info-value">{refsThisRound}</span></div>
                <div className="info-row"><span className="info-label">Всего билетов</span><span className="info-value">{totalTickets}</span></div>
            </div>

            <button type="button" className="action-btn btn-gold" onClick={() => onNavigate('bonuses')}>
                ПРИГЛАСИТЬ ДРУГА — ПОЛУЧИТЬ БИЛЕТ
            </button>
            {myTickets === 0 && (
                <p className="text-muted f-12 text-center">
                    Пока что у вас нет билетов в этом раунде. Зовите друга — билет придёт автоматически.
                </p>
            )}

            <button type="button" className="action-btn btn-outline-gold" style={{ padding: 12 }} onClick={onShowRules}>
                КАК ПРИНЯТЬ УЧАСТИЕ?
            </button>

            <div className="info-card text-center" style={{ borderColor: 'var(--accent)', background: 'rgba(255, 215, 0, 0.05)' }}>
                <div className="text-muted fw-900" style={{ fontSize: 10, marginBottom: 4, textTransform: 'uppercase' }}>
                    До раздачи осталось:
                </div>
                <div className="timer-text">{countdown}</div>
                <div className="text-muted" style={{ fontSize: 9, marginTop: 2 }}>(результаты каждую неделю)</div>
            </div>

            {history && history.length > 0 && (
                <div className="info-card">
                    <h3 className="text-center text-muted fw-900 f-12">ПРЕДЫДУЩИЕ РОЗЫГРЫШИ</h3>
                    {history.slice(0, 5).map((h) => (
                        <div className="top-row" key={h.id}>
                            <span className="top-name">
                                #{h.id} · {new Date(h.drawn_at).toLocaleDateString('ru-RU')}
                            </span>
                            <span className="top-val">{h.pot.toLocaleString('ru-RU')} BSP</span>
                        </div>
                    ))}
                </div>
            )}

            <button type="button" className="back-btn" onClick={() => onNavigate('menu')}>
                НАЗАД В МЕНЮ
            </button>
        </div>
    );
}

function PrizeRow({ label, pct }: { label: string; pct: string }) {
    return (
        <div className="top-row" style={{ padding: '6px 0' }}>
            <span style={{ color: '#fff', fontSize: 11 }}>{label}</span>
            <span className="text-accent fw-900 f-13">{pct}</span>
        </div>
    );
}
